import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChatMessage, AthleteProfile, Reaction } from '../types';
import { triggerHaptic } from '../utils/haptics';
import {
  MessageSquare,
  Send,
  MapPin,
  Users,
  Search,
  Check,
  CheckCheck,
  ChevronRight,
  Sparkles,
  Smile,
  X,
  Play,
  RotateCcw,
  Pin,
  Lock,
  Globe,
  Mic,
  Trash2,
  Loader2,
  Radio,
  Plus,
  Flame,
  MessageCircle,
  Hash,
  ChevronDown,
  ChevronUp,
  User,
  UserPlus,
  UserCheck,
  Filter,
  ArrowRight,
  ArrowLeft,
  Clock,
  Circle
} from 'lucide-react';
import { TeamChatEmptyIllustration } from './illustrations/TeamChatEmptyIllustration';
import {
  fetchTeamChatMessagesFromFirestore,
  subscribeTeamChatMessages,
  fetchDirectMessagesFromFirestore,
  subscribeDirectMessages,
  deleteDirectConversationFromFirestore,
  deleteChannelConversationFromFirestore,
  subscribePresence,
  updatePresenceInFirestore,
  updateMessageStatusInFirestore,
  PresenceDoc
} from '../services/firestoreService';

const QUICK_EMOJIS = ['🔥', '🏀', '💪', '💯', '🙌', '⚡', '👍'];

const QUICK_REPLY_CHIPS = [
  { label: 'Free to play today? 🏀', text: 'Hey, are you free for a pickup run today? 🏀' },
  { label: 'On my way! ⚡', text: 'On my way to the court now! ⚡' },
  { label: 'Running 5 mins late ⏰', text: 'Running about 5 minutes late! ⏰' },
  { label: 'Great game! 💯', text: 'Great run and great game today! 💯' },
  { label: 'What court? 📍', text: 'Which court are you guys playing at? 📍' },
];

interface TeamChatViewProps {
  messages: ChatMessage[];
  user: AthleteProfile;
  allAthletes?: AthleteProfile[];
  onSendMessage: (text: string, type?: 'text' | 'callout' | 'location' | 'game_result', locationName?: string, imageUrl?: string, extra?: Partial<ChatMessage>) => void;
  onSendFriendRequest?: (player: { id: string; name: string; avatar?: string; detail?: string }) => void;
  onJoinGame?: (gameId: string) => void;
  onNavigateToTab?: (tab: string) => void;
  directMessageTarget?: AthleteProfile | null;
  onUpdateUser?: (updated: Partial<AthleteProfile>) => void;
  onDeleteConversation?: (targetId: string, category: 'direct' | 'channel' | 'group') => void;
  onMessagesRead?: (messageIds: string[]) => void;
}

export interface ChatTarget {
  id: string;
  name: string;
  category: 'direct' | 'channel' | 'group';
  status?: string;
  detail?: string;
  avatar?: string;
  sportIcon?: string;
  isOnline?: boolean;
  privacyLevel?: 'Public' | 'Private' | 'InviteOnly';
  authorizedMembers?: string[];
  groupParticipantIds?: string[];
  lastMessageSnippet?: string;
  lastMessageTime?: string;
  lastMessageTimestamp?: number;
  lastMessageSenderId?: string;
  lastMessageRecipientId?: string;
  isLastSenderMe?: boolean;
  unreadCount?: number;
  activePlayerCount?: number;
  position?: string;
  primarySport?: string;
  isPro?: boolean;
}

const extractUrls = (text: string): string[] => {
  if (!text) return [];
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|hoopson\.app\/[^\s]+)/gi;
  return text.match(urlRegex) || [];
};

const FormattedMessageText: React.FC<{ text: string; isMe?: boolean }> = ({ text, isMe }) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|hoopson\.app\/[^\s]+)/gi;
  const parts = text.split(urlRegex);

  return (
    <span>
      {parts.map((part, i) => {
        if (part.match(urlRegex)) {
          const href = part.startsWith('http') ? part : `https://${part}`;
          return (
            <a
              key={i}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className={`underline font-bold transition ${isMe
                ? 'text-black hover:text-indigo-950 decoration-black/40'
                : 'text-lime-300 hover:text-lime-200 decoration-lime-400/40'
                }`}
            >
              {part}
            </a>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
};

const MessageLinkPreview: React.FC<{
  urls: string[];
  isMe?: boolean;
  onOpenVideo?: (url: string, title: string) => void;
  onNavigateToTab?: (tab: string) => void;
}> = ({ urls, isMe, onOpenVideo, onNavigateToTab }) => {
  if (!urls || urls.length === 0) return null;

  return (
    <div className="mt-2 space-y-1.5 w-full max-w-sm">
      {urls.map((url, idx) => {
        const isYouTube = url.includes('youtube.com') || url.includes('youtu.be');
        const isHoopsonApp = url.includes('hoopson.app');

        if (isYouTube) {
          return (
            <div
              key={idx}
              onClick={() => onOpenVideo && onOpenVideo(url, 'Streetball Tournament Highlights')}
              className={`p-2 rounded-2xl border transition flex items-center space-x-2.5 cursor-pointer shadow-sm ${isMe
                ? 'bg-black/10 hover:bg-black/15 border-black/10 text-black'
                : 'bg-indigo-950/90 hover:bg-indigo-900 border-white/10 text-white'
                }`}
            >
              <div className="relative w-12 h-12 rounded-xl overflow-hidden shrink-0 bg-black flex items-center justify-center">
                <img
                  src="https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&q=80&w=200"
                  alt="YouTube Preview"
                  className="w-full h-full object-cover opacity-70"
                />
                <Play className="w-5 h-5 text-white fill-white absolute" />
              </div>
              <div className="truncate flex-1 min-w-0">
                <span className="text-[9px] font-mono uppercase font-black tracking-wider text-red-500">
                  YouTube • Video
                </span>
                <p className="text-xs font-black truncate">Streetball NYC Highlights 2026</p>
                <p className={`text-[10px] truncate ${isMe ? 'text-black/70' : 'text-indigo-300/70'}`}>
                  Watch live breakdown ↗
                </p>
              </div>
            </div>
          );
        }

        if (isHoopsonApp) {
          return (
            <div
              key={idx}
              onClick={() => onNavigateToTab && onNavigateToTab('map')}
              className={`p-2.5 rounded-2xl border transition flex items-center space-x-2.5 cursor-pointer shadow-sm ${isMe
                ? 'bg-black/10 hover:bg-black/15 border-black/10 text-black'
                : 'bg-indigo-950/90 hover:bg-indigo-900 border-lime-400/30 text-white'
                }`}
            >
              <div className="w-10 h-10 rounded-xl bg-lime-400 text-black flex items-center justify-center shrink-0 shadow-md">
                <MapPin className="w-5 h-5 fill-black" />
              </div>
              <div className="truncate flex-1 min-w-0">
                <span className="text-[9px] font-mono uppercase font-black tracking-wider text-lime-400">
                  Court Location Pin
                </span>
                <p className="text-xs font-black truncate">Rucker Park • Court 1 Run</p>
                <p className={`text-[10px] truncate ${isMe ? 'text-black/70' : 'text-indigo-300/70'}`}>
                  Tap to view court on live map ↗
                </p>
              </div>
            </div>
          );
        }

        return (
          <a
            key={idx}
            href={url.startsWith('http') ? url : `https://${url}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={`p-2 rounded-xl border flex items-center space-x-2 transition ${isMe
              ? 'bg-black/5 hover:bg-black/10 border-black/10 text-black'
              : 'bg-indigo-950/80 hover:bg-indigo-900 border-white/10 text-indigo-200'
              }`}
          >
            <Globe className="w-4 h-4 shrink-0 text-lime-400" />
            <span className="text-[11px] truncate font-medium underline flex-1">{url}</span>
          </a>
        );
      })}
    </div>
  );
};

const getMessageNumericTime = (m: ChatMessage): number => {
  if (!m) return 0;
  if (typeof m.createdAt === 'number') return m.createdAt;
  if (typeof m.createdAt === 'string') {
    const t = new Date(m.createdAt).getTime();
    if (!isNaN(t)) return t;
  }
  if (m.timestamp) {
    const t = new Date(m.timestamp).getTime();
    if (!isNaN(t)) return t;
    const today = new Date().toLocaleDateString();
    const parsed = new Date(`${today} ${m.timestamp}`).getTime();
    if (!isNaN(parsed)) return parsed;
  }
  return 0;
};

const formatChatTime = (timeStr?: string, timestamp?: number): string => {
  if (timeStr && (timeStr.includes('AM') || timeStr.includes('PM') || timeStr.includes('ago') || timeStr === 'Yesterday' || timeStr === 'Just now')) {
    return timeStr;
  }
  if (!timestamp) return 'Recently';
  const now = Date.now();
  const diffMinutes = Math.floor((now - timestamp) / (1000 * 60));
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' });
};

// LocalStorage key helper so "started" chats survive refresh per-user
const STARTED_CHATS_STORAGE_PREFIX = 'hoopson_started_chats_';

const loadStartedChatIds = (userId: string): string[] => {
  try {
    const raw = window.localStorage.getItem(STARTED_CHATS_STORAGE_PREFIX + userId);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveStartedChatIds = (userId: string, ids: string[]) => {
  try {
    window.localStorage.setItem(STARTED_CHATS_STORAGE_PREFIX + userId, JSON.stringify(ids));
  } catch {
    // ignore storage errors (private mode, quota, etc.)
  }
};

export const TeamChatView: React.FC<TeamChatViewProps> = ({
  messages = [],
  user,
  allAthletes = [],
  onSendMessage,
  onSendFriendRequest,
  onJoinGame,
  onNavigateToTab,
  directMessageTarget,
  onDeleteConversation,
  onMessagesRead,
}) => {
  const [filterCategory, setFilterCategory] = useState<'all' | 'direct' | 'channel' | 'unread'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [text, setText] = useState('');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatSearch, setNewChatSearch] = useState('');
  const [showEmojiBar, setShowEmojiBar] = useState(false);
  const [showClearHistoryConfirm, setShowClearHistoryConfirm] = useState(false);
  const [videoModal, setVideoModal] = useState<{ url: string; title: string } | null>(null);
  const [sentFriendRequests, setSentFriendRequests] = useState<string[]>([]);
  const [pinnedTargetIds, setPinnedTargetIds] = useState<string[]>([]);
  const [messageReactions, setMessageReactions] = useState<Record<string, Reaction[]>>({});
  const [activeEmojiPickerMsgId, setActiveEmojiPickerMsgId] = useState<string | null>(null);
  const [clearedConversationTimestamps, setClearedConversationTimestamps] = useState<Record<string, number>>({});
  const [locallyReadMessageIds, setLocallyReadMessageIds] = useState<Set<string>>(new Set());
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const [startedChatIds, setStartedChatIds] = useState<string[]>([]);
  const startedChatIdsHydratedRef = useRef(false);

  const [mobileActiveView, setMobileActiveView] = useState<'list' | 'chat'>('chat');

  const [presenceMap, setPresenceMap] = useState<Record<string, PresenceDoc>>({});

  const isAlreadyFriends = useCallback((athleteId: string) => {
    const friendIds = (user?.friendsList || []).map((friend) =>
      typeof friend === 'string' ? friend : friend.id
    );
    return friendIds.includes(athleteId);
  }, [user?.friendsList]);



  useEffect(() => {
    if (!user?.id || startedChatIdsHydratedRef.current) return;
    const loaded = loadStartedChatIds(user.id);
    if (loaded.length > 0) {
      setStartedChatIds(loaded);
    }
    startedChatIdsHydratedRef.current = true;
  }, [user?.id]);

  // Persist startedChatIds whenever they change
  useEffect(() => {
    if (!user?.id || !startedChatIdsHydratedRef.current) return;
    saveStartedChatIds(user.id, startedChatIds);
  }, [startedChatIds, user?.id]);

  const markChatStarted = useCallback((athleteId: string) => {
    setStartedChatIds((prev) => (prev.includes(athleteId) ? prev : [...prev, athleteId]));
  }, []);

  useEffect(() => {
    const unsubPresence = subscribePresence((pMap) => {
      if (pMap) {
        setPresenceMap(pMap);
      }
    });
    return () => {
      unsubPresence();
    };
  }, []);

  const isAthleteOnline = useCallback((athleteId: string, ath?: AthleteProfile): boolean => {
    const pres = presenceMap[athleteId];
    if (pres && pres.isOnline !== undefined) {
      return Boolean(pres.isOnline);
    }
    if (ath && ath.isOnline !== undefined) {
      return Boolean(ath.isOnline);
    }
    const lastActive = (ath as any)?.lastActiveTimestamp;
    if (lastActive) {
      const ts = typeof lastActive === 'number' ? lastActive : new Date(lastActive).getTime();
      if (!isNaN(ts) && Date.now() - ts < 5 * 60 * 1000) return true;
    }
    return ath?.isOnline ?? true;
  }, [presenceMap]);

  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [lazyTeamMessages, setLazyTeamMessages] = useState<ChatMessage[]>([]);
  const [teamMessagesFetched, setTeamMessagesFetched] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const directUnsubscribeRef = useRef<(() => void) | null>(null);
  const teamUnsubscribeRef = useRef<(() => void) | null>(null);
  const fetchedDirectIdsRef = useRef<Set<string>>(new Set());

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3200);
  };

  const baseChannels: ChatTarget[] = useMemo(() => [], []);

  const otherAthletes = useMemo(() => {
    return (allAthletes || []).filter((a) => a.id !== user?.id);
  }, [allAthletes, user?.id]);

  const [customThreadMessages, setCustomThreadMessages] = useState<Record<string, ChatMessage[]>>({});

  const recentConversations = useMemo(() => {
    const currentUserId = user?.id || 'current_user';
    const conversationList: ChatTarget[] = [];
    const directSeenIds = new Set<string>();

    const directMessagesByUser = new Map<string, Map<string, ChatMessage>>();
    const addDirectMessage = (message: ChatMessage) => {
      if (message.chatType !== 'direct' || !message.recipientId || !message.senderId) return;
      const isIncoming = message.recipientId === currentUserId && message.senderId !== currentUserId;
      const isOutgoing = message.senderId === currentUserId && message.recipientId !== currentUserId;
      if (!isIncoming && !isOutgoing) return;

      const otherId = isIncoming ? message.senderId : message.recipientId;
      if (!isIncoming && !startedChatIds.includes(otherId)) return;
      if (!otherId || otherId === currentUserId) return;
      const clearTimestamp = clearedConversationTimestamps[otherId] || 0;
      if (clearTimestamp && getMessageNumericTime(message) <= clearTimestamp) return;
      const thread = directMessagesByUser.get(otherId) || new Map<string, ChatMessage>();
      thread.set(message.id, message);
      directMessagesByUser.set(otherId, thread);
    };

    (messages || []).forEach(addDirectMessage);
    Object.values(customThreadMessages).flat().forEach(addDirectMessage);

    // 1) Conversations that have REAL message history (either direction)
    directMessagesByUser.forEach((threadMap, otherId) => {
      const ath = otherAthletes.find((a) => a.id === otherId);
      const sortedThread = Array.from(threadMap.values()).sort((a, b) => getMessageNumericTime(a) - getMessageNumericTime(b));
      const lastMsg = sortedThread[sortedThread.length - 1];
      const unreadCount = sortedThread.filter((message) =>
        message.senderId !== currentUserId &&
        message.readStatus !== 'read' &&
        !locallyReadMessageIds.has(message.id)
      ).length;
      const isOnline = isAthleteOnline(otherId, ath);

      conversationList.push({
        id: otherId,
        name: ath?.name || lastMsg.senderName || 'Athlete',
        category: 'direct',
        detail: ath ? `${ath.position || 'Athlete'} • ${ath.primarySport || 'Basketball'}` : '1-on-1 Direct Chat',
        status: ath?.isPro ? '⚡ PRO Athlete' : isOnline ? 'Online 🟢' : 'Offline',
        avatar: ath?.avatar || lastMsg.senderAvatar,
        isOnline,
        isPro: ath?.isPro,
        position: ath?.position,
        primarySport: ath?.primarySport,
        lastMessageSnippet: lastMsg.text,
        lastMessageTime: formatChatTime(lastMsg.timestamp, getMessageNumericTime(lastMsg)),
        lastMessageTimestamp: getMessageNumericTime(lastMsg),
        lastMessageSenderId: lastMsg.senderId,
        lastMessageRecipientId: lastMsg.recipientId,
        isLastSenderMe: lastMsg.senderId === currentUserId,
        unreadCount,
      });
      directSeenIds.add(otherId);
    });

    baseChannels.forEach((ch) => {
      const channelMsgs = (lazyTeamMessages.length > 0 ? lazyTeamMessages : messages).filter(
        (m) => m.gameId === ch.id || (!m.chatType || m.chatType === 'channel')
      );
      const lastMsg = channelMsgs.length > 0 ? channelMsgs[channelMsgs.length - 1] : null;
      const snippet = lastMsg ? lastMsg.text : ch.lastMessageSnippet;
      const ts = lastMsg ? getMessageNumericTime(lastMsg) : (ch.lastMessageTimestamp || 0);

      conversationList.push({
        ...ch,
        lastMessageSnippet: snippet,
        lastMessageTime: formatChatTime(lastMsg?.timestamp || ch.lastMessageTime, ts),
        lastMessageTimestamp: ts,
        lastMessageSenderId: lastMsg?.senderId,
        lastMessageRecipientId: lastMsg?.recipientId,
        isLastSenderMe: lastMsg ? (lastMsg.senderId === currentUserId) : false,
        unreadCount: 0,
      });
    });


    return conversationList.sort((a, b) => {
      const aPinned = pinnedTargetIds.includes(a.id) ? 1 : 0;
      const bPinned = pinnedTargetIds.includes(b.id) ? 1 : 0;
      if (aPinned !== bPinned) return bPinned - aPinned;
      return (b.lastMessageTimestamp || 0) - (a.lastMessageTimestamp || 0);
    });
  }, [customThreadMessages, messages, otherAthletes, baseChannels, lazyTeamMessages, user?.id, clearedConversationTimestamps, pinnedTargetIds, isAthleteOnline, locallyReadMessageIds]);

  const computeDefaultTarget = useCallback((): ChatTarget => {
    if (directMessageTarget) {
      return {
        id: directMessageTarget.id,
        name: directMessageTarget.name,
        category: 'direct',
        detail: `${directMessageTarget.position || 'Athlete'} • ${directMessageTarget.primarySport || 'Basketball'}`,
        status: 'Online 🟢',
        avatar: directMessageTarget.avatar,
        isOnline: true,
        isPro: directMessageTarget.isPro,
        lastMessageSnippet: `Direct chat with ${directMessageTarget.name}`,
        lastMessageTime: 'Just now',
        lastMessageTimestamp: Date.now()
      };
    }

    const currentUserId = user?.id || 'current_user';

    let latestIncomingTarget: ChatTarget | null = null;
    let maxIncomingTimestamp = 0;

    recentConversations.forEach((conv) => {
      if (conv.category === 'direct' && !conv.isLastSenderMe && (conv.lastMessageTimestamp || 0) > maxIncomingTimestamp) {
        maxIncomingTimestamp = conv.lastMessageTimestamp || 0;
        latestIncomingTarget = conv;
      }
    });

    let latestOverallTarget: ChatTarget | null = null;
    let maxOverallTimestamp = 0;

    recentConversations.forEach((conv) => {
      if ((conv.lastMessageTimestamp || 0) > maxOverallTimestamp) {
        maxOverallTimestamp = conv.lastMessageTimestamp || 0;
        latestOverallTarget = conv;
      }
    });


    if (latestIncomingTarget && maxIncomingTimestamp >= maxOverallTimestamp - 1000 * 60 * 60) {
      return latestIncomingTarget;
    }

    if (latestOverallTarget && maxOverallTimestamp > 0) {
      return latestOverallTarget;
    }

    if (recentConversations.length > 0) {
      return recentConversations[0];
    }

    // Brand-new user with zero conversations: show an empty/neutral placeholder
    // rather than defaulting into a channel or random athlete.
    return {
      id: 'no_active_chat',
      name: 'No conversations yet',
      category: 'channel',
      detail: 'Start a new chat or wait for someone to message you',
      privacyLevel: 'Public',
      sportIcon: '💬',
      lastMessageSnippet: 'No messages yet',
      lastMessageTime: '',
      lastMessageTimestamp: 0,
      unreadCount: 0,
    };
  }, [directMessageTarget, recentConversations, user?.id]);

  const [selectedTarget, setSelectedTarget] = useState<ChatTarget>(() => computeDefaultTarget());

  const selectedUserPresence = selectedTarget.category === 'direct'
    ? presenceMap[selectedTarget.id]
    : undefined;

  const updateCurrentPresence = useCallback((isTyping: boolean) => {
    if (!user?.id) return;
    updatePresenceInFirestore({
      userId: user.id,
      isOnline: true,
      isTyping,
      typingTargetId: isTyping && selectedTarget.category === 'direct' ? selectedTarget.id : undefined,
      lastActive: Date.now(),
    });
  }, [selectedTarget.category, selectedTarget.id, user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    updatePresenceInFirestore({ userId: user.id, isOnline: true, isTyping: false, lastActive: Date.now() });
    return () => {
      updatePresenceInFirestore({ userId: user.id, isOnline: false, isTyping: false, lastActive: Date.now() });
    };
  }, [user?.id]);

  // Keep the selected target in sync once conversations resolve for the
  // first time (e.g. after presence/messages load in), but only while the
  // user hasn't manually picked something and there's no chat selected yet.
  const hasAutoSelectedRef = useRef(false);
  useEffect(() => {
    if (hasAutoSelectedRef.current) return;
    if (directMessageTarget) return;
    if (selectedTarget.id !== 'no_active_chat') {
      hasAutoSelectedRef.current = true;
      return;
    }
    if (recentConversations.length > 0) {
      setSelectedTarget(recentConversations[0]);
      hasAutoSelectedRef.current = true;
    }
  }, [recentConversations, directMessageTarget, selectedTarget.id]);

  useEffect(() => {
    if (directMessageTarget) {
      markChatStarted(directMessageTarget.id);
      const targetItem: ChatTarget = {
        id: directMessageTarget.id,
        name: directMessageTarget.name,
        category: 'direct',
        detail: `${directMessageTarget.position || 'Athlete'} • ${directMessageTarget.primarySport || 'Basketball'}`,
        status: 'Online 🟢',
        avatar: directMessageTarget.avatar,
        isOnline: true,
        isPro: directMessageTarget.isPro,
        lastMessageSnippet: `Direct chat with ${directMessageTarget.name}`,
        lastMessageTime: 'Just now',
        lastMessageTimestamp: Date.now()
      };
      setSelectedTarget(targetItem);
      setMobileActiveView('chat');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directMessageTarget]);

  // Whenever a NEW incoming direct message arrives from someone not yet in
  // startedChatIds, automatically mark that thread as started so it shows
  // up in the sidebar (this is the "someone sends me a chat" case).
  useEffect(() => {
    const currentUserId = user?.id || 'current_user';
    const incomingSenderIds = new Set<string>();

    (messages || []).forEach((m) => {
      if (m.chatType === 'direct' && m.recipientId === currentUserId && m.senderId && m.senderId !== currentUserId) {
        incomingSenderIds.add(m.senderId);
      }
    });
    Object.values(customThreadMessages).flat().forEach((m) => {
      if (m.chatType === 'direct' && m.recipientId === currentUserId && m.senderId && m.senderId !== currentUserId) {
        incomingSenderIds.add(m.senderId);
      }
    });

    if (incomingSenderIds.size === 0) return;

    setStartedChatIds((prev) => {
      const toAdd = Array.from(incomingSenderIds).filter((id) => !prev.includes(id));
      if (toAdd.length === 0) return prev;
      return [...prev, ...toAdd];
    });
  }, [messages, customThreadMessages, user?.id]);

  useEffect(() => {
    const targetId = selectedTarget.id;
    const isDirect = selectedTarget.category === 'direct';

    if (targetId === 'no_active_chat') {
      return;
    }

    if (isDirect) {
      if (teamUnsubscribeRef.current) {
        teamUnsubscribeRef.current();
        teamUnsubscribeRef.current = null;
      }
      if (directUnsubscribeRef.current) {
        directUnsubscribeRef.current();
        directUnsubscribeRef.current = null;
      }

      if (!fetchedDirectIdsRef.current.has(targetId) && user?.id) {
        setIsLoadingMessages(true);
        fetchDirectMessagesFromFirestore(user.id, targetId)
          .then((remoteMsgs) => {
            if (remoteMsgs && remoteMsgs.length > 0) {
              setCustomThreadMessages((prev) => {
                const existing = prev[targetId] || [];
                const mergedMap = new Map<string, ChatMessage>();
                [...existing, ...remoteMsgs].forEach((m) => {
                  if (m && m.id) mergedMap.set(m.id, m);
                });
                return { ...prev, [targetId]: Array.from(mergedMap.values()) };
              });
            }
            fetchedDirectIdsRef.current.add(targetId);
          })
          .catch((err) => {
            console.error('Error fetching direct messages on-demand:', err);
          })
          .finally(() => {
            setIsLoadingMessages(false);
          });
      }

      if (user?.id) {
        const unsub = subscribeDirectMessages(user.id, targetId, (remoteMsgs) => {
          if (remoteMsgs && remoteMsgs.length > 0) {
            setCustomThreadMessages((prev) => {
              const existing = prev[targetId] || [];
              const mergedMap = new Map<string, ChatMessage>();
              [...existing, ...remoteMsgs].forEach((m) => {
                if (m && m.id) mergedMap.set(m.id, m);
              });
              return { ...prev, [targetId]: Array.from(mergedMap.values()) };
            });
          }
        });
        directUnsubscribeRef.current = unsub;
      }
    } else {
      if (directUnsubscribeRef.current) {
        directUnsubscribeRef.current();
        directUnsubscribeRef.current = null;
      }

      if (!teamMessagesFetched) {
        setIsLoadingMessages(true);
        fetchTeamChatMessagesFromFirestore()
          .then((msgs) => {
            setLazyTeamMessages(msgs);
            setTeamMessagesFetched(true);
          })
          .catch((err) => {
            console.error('Error on-demand fetching team messages:', err);
          })
          .finally(() => {
            setIsLoadingMessages(false);
          });
      }

      if (!teamUnsubscribeRef.current) {
        const unsub = subscribeTeamChatMessages((msgs) => {
          setLazyTeamMessages(msgs);
        });
        teamUnsubscribeRef.current = unsub;
      }
    }

    return () => {
      if (directUnsubscribeRef.current) {
        directUnsubscribeRef.current();
        directUnsubscribeRef.current = null;
      }
    };
  }, [selectedTarget.id, selectedTarget.category, user?.id, teamMessagesFetched]);

  useEffect(() => {
    return () => {
      if (directUnsubscribeRef.current) {
        directUnsubscribeRef.current();
        directUnsubscribeRef.current = null;
      }
      if (teamUnsubscribeRef.current) {
        teamUnsubscribeRef.current();
        teamUnsubscribeRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const currentUserId = user?.id || 'current_user';
    const targetId = selectedTarget.id;
    const isDirect = selectedTarget.category === 'direct';

    if (isDirect) {
      const threadMap = new Map<string, ChatMessage>();
      [...(customThreadMessages[targetId] || []), ...(messages || [])].forEach((message) => {
        const isTargetMessage =
          (message.senderId === currentUserId && message.recipientId === targetId) ||
          (message.senderId === targetId && message.recipientId === currentUserId);
        if (isTargetMessage && message.id) threadMap.set(message.id, message);
      });
      const unreadMsgs = Array.from(threadMap.values()).filter((m) =>
        m.senderId !== currentUserId &&
        m.readStatus !== 'read' &&
        !locallyReadMessageIds.has(m.id)
      );
      if (unreadMsgs.length > 0) {
        setLocallyReadMessageIds((prev) => {
          const next = new Set(prev);
          unreadMsgs.forEach((m) => next.add(m.id));
          return next;
        });

        setCustomThreadMessages((prev) => {
          const curr = prev[targetId] || [];
          return {
            ...prev,
            [targetId]: curr.map((m) =>
              m.senderId !== currentUserId ? { ...m, readStatus: 'read', messageStatus: 'read' } : m
            ),
          };
        });

        unreadMsgs.forEach((m) => {
          if (m.id && !m.id.startsWith('dm1_') && !m.id.startsWith('dm2_') && !m.id.startsWith('dm3_')) {
            updateMessageStatusInFirestore(m.id, 'read');
          }
        });
        onMessagesRead?.(unreadMsgs.map((message) => message.id));
      }
    }
  }, [selectedTarget.id, selectedTarget.category, customThreadMessages, messages, locallyReadMessageIds, user?.id, onMessagesRead]);

  const handleSelectConversation = (target: ChatTarget) => {
    triggerHaptic('light');
    setSelectedTarget(target);
    setMobileActiveView('chat');
    hasAutoSelectedRef.current = true;

    if (target.category === 'direct') {
      markChatStarted(target.id);
    }

    const currentUserId = user?.id || 'current_user';
    if (target.category === 'direct') {
      setCustomThreadMessages((prev) => {
        const curr = prev[target.id] || [];
        const hasUnread = curr.some((m) => m.senderId !== currentUserId && m.readStatus !== 'read');
        if (!hasUnread) return prev;
        return {
          ...prev,
          [target.id]: curr.map((m) =>
            m.senderId !== currentUserId ? { ...m, readStatus: 'read', messageStatus: 'read' } : m
          ),
        };
      });
    }
  };

  const handleStartNewChatWithAthlete = (athlete: AthleteProfile) => {
    triggerHaptic('medium');
    markChatStarted(athlete.id);
    hasAutoSelectedRef.current = true;

    const targetItem: ChatTarget = {
      id: athlete.id,
      name: athlete.name,
      category: 'direct',
      detail: `${athlete.position || 'Athlete'} • ${athlete.primarySport || 'Basketball'}`,
      status: athlete.isPro ? '⚡ PRO Athlete • Online' : 'Online 🟢',
      avatar: athlete.avatar,
      isOnline: athlete.isOnline ?? true,
      isPro: athlete.isPro,
      position: athlete.position,
      primarySport: athlete.primarySport,
      lastMessageSnippet: `Direct chat with ${athlete.name}`,
      lastMessageTime: 'Just now',
      lastMessageTimestamp: Date.now()
    };

    setSelectedTarget(targetItem);
    setShowNewChatModal(false);
    setMobileActiveView('chat');
    showToast(`Started conversation with ${athlete.name} 💬`);
  };

  const togglePinTarget = (targetId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    triggerHaptic('light');
    setPinnedTargetIds((prev) =>
      prev.includes(targetId) ? prev.filter((id) => id !== targetId) : [...prev, targetId]
    );
  };

  const currentMessages = useMemo(() => {
    const targetId = selectedTarget.id;
    const currentUserId = user?.id || 'current_user';
    const clearTimestamp = clearedConversationTimestamps[targetId] || 0;

    if (targetId === 'no_active_chat') {
      return [];
    }

    if (selectedTarget.category === 'channel') {
      const channelMsgs = (lazyTeamMessages.length > 0 ? lazyTeamMessages : messages).filter((m) => {
        if (clearTimestamp && getMessageNumericTime(m) <= clearTimestamp) return false;
        if (m.chatType === 'direct' && m.recipientId) return false;
        if (targetId === 'general' || targetId === 'all') return true;
        if (m.gameId === targetId) return true;
        if (targetId.startsWith('channel_')) {
          return !m.chatType || m.chatType === 'channel' || m.chatType === 'group';
        }
        return true;
      });
      return [...channelMsgs].sort((a, b) => getMessageNumericTime(a) - getMessageNumericTime(b));
    }

    const localThread = customThreadMessages[targetId] || [];
    const directFromProp = (messages || []).filter((m) => {
      if (clearTimestamp && getMessageNumericTime(m) <= clearTimestamp) return false;
      const isDirectToTarget = (m.senderId === currentUserId && m.recipientId === targetId);
      const isDirectFromTarget = (m.senderId === targetId && m.recipientId === currentUserId);
      const isLegacyMatch = m.chatType === 'direct' && (
        (m.senderId === currentUserId && m.recipientId === targetId) ||
        (m.senderId === targetId && m.recipientId === currentUserId)
      );
      return isDirectToTarget || isDirectFromTarget || isLegacyMatch;
    });

    const combinedMap = new Map<string, ChatMessage>();
    [...localThread, ...directFromProp].forEach((m) => {
      if (m && m.id) {
        if (!clearTimestamp || getMessageNumericTime(m) > clearTimestamp) {
          combinedMap.set(m.id, m);
        }
      }
    });

    const result = Array.from(combinedMap.values());
    result.sort((a, b) => getMessageNumericTime(a) - getMessageNumericTime(b));
    return result;
  }, [messages, customThreadMessages, lazyTeamMessages, selectedTarget, user?.id, clearedConversationTimestamps]);

    useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [currentMessages, selectedTarget.id]);

  const filteredConversations = useMemo(() => {
    const currentUserId = user?.id || 'current_user';
    let list = recentConversations;

    if (filterCategory === 'direct') {
      list = list.filter((c) => c.category === 'direct');
    } else if (filterCategory === 'channel') {
      list = list.filter((c) => c.category === 'channel');
    } else if (filterCategory === 'unread') {
      list = list.filter((c) => (c.unreadCount || 0) > 0);
    }

    list = list.map((c) => {
      if (c.category !== 'direct') return c;
      const hasRealMsg = (c.lastMessageTimestamp || 0) > 0;
      if (!hasRealMsg) return c;

      const belongsToMe =
        c.lastMessageSenderId === currentUserId ||
        c.lastMessageRecipientId === currentUserId;

      if (!belongsToMe) {
        return {
          ...c,
          lastMessageSnippet: 'Start a direct chat with ' + c.name,
          lastMessageTime: '',
          lastMessageTimestamp: 0,
          lastMessageSenderId: undefined,
          lastMessageRecipientId: undefined,
          isLastSenderMe: false,
          unreadCount: 0,
        };
      }
      return c;
    });

    // Only keep direct conversations that have BOTH a sender and a
    // recipient set on their last message — i.e. an actual message was
    // exchanged. Placeholder/"started but never messaged" entries with
    // empty lastMessageSenderId or lastMessageRecipientId are dropped
    // from the visible list.
    list = list.filter((c) => {
      if (c.category !== 'direct') return true;
      return Boolean(c.lastMessageSenderId) && Boolean(c.lastMessageRecipientId);
    });

    const query = searchQuery.trim().toLowerCase();
    if (query) {
      list = list.filter((c) => {
        const matchName = c.name.toLowerCase().includes(query);
        const matchDetail = c.detail && c.detail.toLowerCase().includes(query);
        const matchSnippet = c.lastMessageSnippet && c.lastMessageSnippet.toLowerCase().includes(query);
        const matchSport = c.primarySport && c.primarySport.toLowerCase().includes(query);
        const matchPos = c.position && c.position.toLowerCase().includes(query);
        return matchName || matchDetail || matchSnippet || matchSport || matchPos;
      });
    }

    const sorted = [...list].sort((a, b) => {
      const aPinned = pinnedTargetIds.includes(a.id) ? 1 : 0;
      const bPinned = pinnedTargetIds.includes(b.id) ? 1 : 0;
      if (aPinned !== bPinned) return bPinned - aPinned;

      const aUnread = (a.unreadCount || 0) > 0 ? 1 : 0;
      const bUnread = (b.unreadCount || 0) > 0 ? 1 : 0;
      if (aUnread !== bUnread) return bUnread - aUnread;

      const aHasChat = (a.lastMessageTimestamp || 0) > 0 ? 1 : 0;
      const bHasChat = (b.lastMessageTimestamp || 0) > 0 ? 1 : 0;
      if (aHasChat !== bHasChat) return bHasChat - aHasChat;

      return (b.lastMessageTimestamp || 0) - (a.lastMessageTimestamp || 0);
    });

    return sorted;
  }, [recentConversations, filterCategory, searchQuery, pinnedTargetIds, user?.id]);

  const filteredNewChatAthletes = useMemo(() => {
    const query = newChatSearch.trim().toLowerCase();
    if (!query) return otherAthletes;
    return otherAthletes.filter(
      (a) =>
        (a.name && a.name.toLowerCase().includes(query)) ||
        (a.handle && a.handle.toLowerCase().includes(query)) ||
        (a.primarySport && a.primarySport.toLowerCase().includes(query)) ||
        (a.position && a.position.toLowerCase().includes(query)) ||
        (a.schoolOrLeague && a.schoolOrLeague.toLowerCase().includes(query)) ||
        (a.registeredCity && a.registeredCity.toLowerCase().includes(query))
    );
  }, [otherAthletes, newChatSearch]);


  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (selectedTarget.id === 'no_active_chat') return;

    triggerHaptic('medium');
    const msgText = text.trim();
    const isDirect = selectedTarget.category === 'direct';

    if (isDirect) {
      markChatStarted(selectedTarget.id);
    }

    const newMsg: ChatMessage = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      senderId: user?.id || 'current_user',
      senderName: user?.name || 'Me',
      senderAvatar: user?.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=200',
      text: msgText,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      createdAt: Date.now(),
      chatType: isDirect ? 'direct' : 'channel',
      recipientId: isDirect ? selectedTarget.id : undefined,
      gameId: !isDirect ? selectedTarget.id : undefined,
      messageStatus: 'delivered',
      readStatus: 'read'
    };

    if (isDirect) {
      setCustomThreadMessages((prev) => ({
        ...prev,
        [selectedTarget.id]: [...(prev[selectedTarget.id] || []), newMsg],
      }));
    } else {
      setLazyTeamMessages((prev) => [...prev, newMsg]);
    }

    onSendMessage(msgText, 'text', undefined, undefined, {
      id: newMsg.id,
      createdAt: newMsg.createdAt,
      chatType: isDirect ? 'direct' : 'channel',
      recipientId: isDirect ? selectedTarget.id : undefined,
      gameId: !isDirect ? selectedTarget.id : undefined,
    });

    setText('');
    setShowEmojiBar(false);
  };

  const handleQuickReply = (quickText: string) => {
    triggerHaptic('light');
    setText(quickText);
  };

  const handleShareLocation = () => {
    triggerHaptic('medium');
    const locText = '📍 Rucker Park (Court 1) - Pickup run starting now! hoopson.app/courts/rucker_park_1';
    setText(locText);
  };

  const handleClearConversation = async () => {
    triggerHaptic('heavy');
    const targetId = selectedTarget.id;
    const isDirect = selectedTarget.category === 'direct';
    const clearNow = Date.now();

    setClearedConversationTimestamps((prev) => ({
      ...prev,
      [targetId]: clearNow,
    }));

    if (isDirect) {
      // Fully wipe local message state for this thread
      setCustomThreadMessages((prev) => {
        const next = { ...prev };
        delete next[targetId];
        return next;
      });

      // Remove this conversation entirely from the sidebar — clearing means
      // it disappears completely, not just "empty but still listed".
      setStartedChatIds((prev) => prev.filter((id) => id !== targetId));
    } else {
      setLazyTeamMessages([]);
    }

    if (onDeleteConversation) {
      onDeleteConversation(targetId, selectedTarget.category);
    }

    try {
      if (isDirect) {
        await deleteDirectConversationFromFirestore(user?.id || 'current_user', targetId);
      } else {
        await deleteChannelConversationFromFirestore(targetId);
      }
    } catch (err) {
      console.error('Failed to delete conversation from Firestore:', err);
    }

    setShowClearHistoryConfirm(false);
    showToast('Conversation & all messages cleared 🧹');

    hasAutoSelectedRef.current = false;
    setMobileActiveView('list');
  };

  const handleToggleReaction = (msgId: string, emoji: string) => {
    triggerHaptic('light');
    setMessageReactions((prev) => {
      const currentList = prev[msgId] || [];
      const userIndex = currentList.findIndex((r) => r.emoji === emoji && r.users.includes(user?.id || 'me'));

      if (userIndex > -1) {
        const updated = currentList.map((r, idx) => {
          if (idx === userIndex) {
            return {
              ...r,
              count: r.count - 1,
              users: r.users.filter((u) => u !== (user?.id || 'me')),
            };
          }
          return r;
        }).filter((r) => r.count > 0);
        return { ...prev, [msgId]: updated };
      } else {
        const emojiIndex = currentList.findIndex((r) => r.emoji === emoji);
        if (emojiIndex > -1) {
          const updated = [...currentList];
          updated[emojiIndex] = {
            ...updated[emojiIndex],
            count: updated[emojiIndex].count + 1,
            users: [...updated[emojiIndex].users, user?.id || 'me'],
          };
          return { ...prev, [msgId]: updated };
        } else {
          return {
            ...prev,
            [msgId]: [...currentList, { emoji, count: 1, users: [user?.id || 'me'] }],
          };
        }
      }
    });
    setActiveEmojiPickerMsgId(null);
  };

  const toggleVoiceRecording = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      showToast('Voice dictation is not supported on this browser.');
      return;
    }

    if (isRecording) {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsRecording(false);
      return;
    }

    try {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsRecording(true);
        triggerHaptic('medium');
      };

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        if (transcript) {
          setText((prev) => (prev ? `${prev} ${transcript}` : transcript));
          showToast(`Voice transcribed: "${transcript}"`);
        }
      };

      recognition.onerror = () => {
        setIsRecording(false);
      };

      recognition.onend = () => {
        setIsRecording(false);
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (e) {
      console.error('Speech recognition error:', e);
      setIsRecording(false);
    }
  };

  const totalUnread = useMemo(() => {
    return recentConversations.reduce((acc, c) => acc + (c.unreadCount || 0), 0);
  }, [recentConversations]);

  const isEmptyChatState = selectedTarget.id === 'no_active_chat';

  return (
    <div id="team-chat-view-container" className="flex h-[calc(100dvh-5rem)] min-h-0 flex-col gap-3 overflow-hidden pb-2 text-white animate-fadeIn">
      {/* Top Header Bar */}
      <div id="chat-top-header" className="bg-indigo-900/70 p-4 sm:p-5 rounded-3xl border border-white/10 shadow-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-2xl bg-lime-400 text-black flex items-center justify-center font-black shadow-md shadow-lime-400/20 shrink-0">
            <MessageSquare className="w-5 h-5 fill-black" />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <h1 className="text-xl font-black italic uppercase tracking-tight text-white">
                Playground League Chat
              </h1>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-extrabold uppercase bg-lime-400/20 text-lime-300 border border-lime-400/30">
                Live &bull; Active
              </span>
            </div>
            <p className="text-xs text-indigo-200/70 font-semibold mt-0.5">
              Direct messages with community athletes & live court broadcast channels
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            id="btn-start-new-chat"
            type="button"
            onClick={() => {
              triggerHaptic('medium');
              setShowNewChatModal(true);
            }}
            className="w-full sm:w-auto px-4 py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black uppercase italic text-xs rounded-2xl shadow-lg shadow-lime-400/20 transition flex items-center justify-center space-x-2"
          >
            <Plus className="w-4 h-4 stroke-[3]" />
            <span>New Chat</span>
          </button>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-hidden lg:grid-cols-12">
        <div
          id="chat-inbox-sidebar"
          className={`lg:col-span-5 xl:col-span-4 min-h-0 bg-indigo-900/60 p-3 sm:p-4 rounded-3xl border border-white/10 shadow-xl flex flex-col space-y-3 ${mobileActiveView === 'chat' ? 'hidden lg:flex' : 'flex'
            }`}
        >

          <div className="relative">
            <Search className="w-4 h-4 text-indigo-300 absolute left-3 top-3" />
            <input
              id="chat-search-input"
              type="text"
              placeholder="Search conversations, athletes, sports..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-8 py-2.5 bg-indigo-950 text-white text-xs font-semibold rounded-2xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400 placeholder:text-indigo-300/50 shadow-inner"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-3 text-indigo-300 hover:text-white"
                title="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="flex items-center space-x-1.5 overflow-x-auto no-scrollbar pb-1">
            <button
              type="button"
              onClick={() => {
                triggerHaptic('light');
                setFilterCategory('all');
              }}
              className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase italic transition whitespace-nowrap flex items-center space-x-1.5 ${filterCategory === 'all'
                ? 'bg-lime-400 text-black shadow-md font-bold'
                : 'bg-indigo-950/80 text-indigo-200 hover:text-white border border-white/5'
                }`}
            >
              <span>All Chats</span>
              <span className={`text-[10px] font-mono px-1 rounded-full ${filterCategory === 'all' ? 'bg-black/20 text-black' : 'bg-indigo-900 text-indigo-300'}`}>
                {recentConversations.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => {
                triggerHaptic('light');
                setFilterCategory('direct');
              }}
              className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase italic transition whitespace-nowrap flex items-center space-x-1.5 ${filterCategory === 'direct'
                ? 'bg-lime-400 text-black shadow-md font-bold'
                : 'bg-indigo-950/80 text-indigo-200 hover:text-white border border-white/5'
                }`}
            >
              <MessageCircle className="w-3 h-3" />
              <span>Direct (DMs)</span>
            </button>

            <button
              type="button"
              onClick={() => {
                triggerHaptic('light');
                setFilterCategory('channel');
              }}
              className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase italic transition whitespace-nowrap flex items-center space-x-1.5 ${filterCategory === 'channel'
                ? 'bg-lime-400 text-black shadow-md font-bold'
                : 'bg-indigo-950/80 text-indigo-200 hover:text-white border border-white/5'
                }`}
            >
              <Hash className="w-3 h-3" />
              <span>Channels</span>
            </button>

            {totalUnread > 0 && (
              <button
                type="button"
                onClick={() => {
                  triggerHaptic('light');
                  setFilterCategory('unread');
                }}
                className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase italic transition whitespace-nowrap flex items-center space-x-1 ${filterCategory === 'unread'
                  ? 'bg-lime-400 text-black shadow-md font-bold'
                  : 'bg-indigo-950/80 text-lime-400 hover:text-white border border-lime-400/20'
                  }`}
              >
                <Circle className="w-2 h-2 fill-lime-400 text-lime-400" />
                <span>Unread ({totalUnread})</span>
              </button>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto space-y-1.5 pr-0.5">
            {filteredConversations.length === 0 ? (
              <div className="p-8 text-center text-xs text-indigo-300/70 bg-indigo-950/40 rounded-2xl border border-white/5 space-y-2">
                <MessageSquare className="w-8 h-8 mx-auto text-indigo-400/50" />
                <p className="font-bold text-white">No conversations yet</p>
                <p>Search for an athlete to start a chat, or wait for someone to message you.</p>
                <button
                  type="button"
                  onClick={() => setShowNewChatModal(true)}
                  className="mt-2 px-3 py-1.5 bg-lime-400 text-black text-xs font-black uppercase rounded-xl inline-block"
                >
                  + New Chat
                </button>
              </div>
            ) : (
              filteredConversations.map((conv) => {
                const isSelected = selectedTarget.id === conv.id;
                const isPinned = pinnedTargetIds.includes(conv.id);
                return (
                  <motion.button
                    layout
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    key={conv.id}
                    id={`conversation-item-${conv.id}`}
                    type="button"
                    onClick={() => handleSelectConversation(conv)}
                    className={`w-full p-3 rounded-2xl text-left transition flex items-center justify-between border relative group ${isSelected
                      ? 'bg-indigo-950 text-white border-lime-400 ring-1 ring-lime-400/40 shadow-lg'
                      : 'bg-indigo-950/70 text-white hover:bg-indigo-950/90 border-white/5 hover:border-white/20'
                      }`}
                  >

                    {isSelected && (
                      <div className="absolute left-0 top-2 bottom-2 w-1.5 bg-lime-400 rounded-r-full shadow-md shadow-lime-400" />
                    )}

                    <div className="flex items-center space-x-3 truncate pl-1 flex-1 min-w-0">

                      <div className="relative shrink-0">
                        {conv.category === 'direct' ? (
                          conv.avatar ? (
                            <img
                              src={conv.avatar}
                              alt={conv.name}
                              className={`w-10 h-10 rounded-full object-cover ring-2 ${isSelected ? 'ring-lime-400' : 'ring-white/10'
                                }`}
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-lime-400 text-black flex items-center justify-center font-black text-sm">
                              {conv.name.charAt(0)}
                            </div>
                          )
                        ) : (
                          <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-sm font-black border ${isSelected ? 'bg-black text-lime-400 border-lime-400' : 'bg-indigo-900 text-lime-300 border-lime-400/20'
                            }`}>
                            {conv.sportIcon || <Hash className="w-5 h-5" />}
                          </div>
                        )}

                        {conv.category === 'direct' && conv.isOnline && (
                          <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-lime-400 border-2 border-indigo-950 shadow-sm shadow-lime-400" />
                        )}
                      </div>

                      <div className="truncate min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1.5 truncate">
                            <p className={`text-xs font-black truncate ${isSelected ? 'text-lime-300' : 'text-white'}`}>
                              {conv.name}
                            </p>

                            {conv.category === 'direct' && conv.isOnline && (
                              <span className="relative flex h-2 w-2 shrink-0 ml-0.5" title="Online now">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-lime-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-lime-400 shadow-sm shadow-lime-400"></span>
                              </span>
                            )}

                            {conv.isPro && (
                              <span className="text-[9px] font-mono px-1 rounded bg-lime-400/20 text-lime-300 border border-lime-400/30">
                                PRO
                              </span>
                            )}
                            {isPinned && (
                              <Pin className="w-2.5 h-2.5 fill-amber-400 text-amber-400 shrink-0" />
                            )}
                          </div>

                          {conv.lastMessageTime && (
                            <span className={`text-[10px] font-mono shrink-0 ml-1.5 ${(conv.unreadCount || 0) > 0 ? 'text-lime-400 font-bold' : isSelected ? 'text-lime-400' : 'text-indigo-300/70'
                              }`}>
                              {conv.lastMessageTime}
                            </span>
                          )}
                        </div>

                        {/* Message Snippet with Unread Highlight */}
                        <p className={`text-[11px] truncate mt-0.5 flex items-center ${(conv.unreadCount || 0) > 0
                          ? 'text-white font-bold'
                          : isSelected ? 'text-indigo-200 font-medium' : 'text-indigo-300/70'
                          }`}>
                          {(conv.unreadCount || 0) > 0 && (
                            <span className="w-1.5 h-1.5 rounded-full bg-lime-400 inline-block mr-1.5 shrink-0 shadow-sm shadow-lime-400 animate-pulse" />
                          )}
                          {conv.isLastSenderMe && (
                            <span className="text-indigo-400 font-semibold mr-1 shrink-0">You:</span>
                          )}
                          <span className="truncate">{conv.lastMessageSnippet || conv.detail || 'Active conversation'}</span>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-1 shrink-0 ml-2">
                      {(() => {
                        const unreadCount = conv.unreadCount || 0;
                        return unreadCount > 0 ? (
                          <span
                            id={`unread-badge-${conv.id}`}
                            className="px-2 py-0.5 min-w-[20px] h-5 rounded-full bg-lime-400 text-black font-black text-[10px] flex items-center justify-center shadow-md shadow-lime-400/40 animate-pulse"
                            title={`${unreadCount} unread message${unreadCount > 1 ? 's' : ''}`}
                          >
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => togglePinTarget(conv.id, e)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-indigo-400 hover:text-amber-400 transition"
                            title={isPinned ? "Unpin" : "Pin"}
                          >
                            <Pin className={`w-3 h-3 ${isPinned ? 'fill-amber-400 text-amber-400' : ''}`} />
                          </button>
                        );
                      })()}
                    </div>
                  </motion.button>
                );
              })
            )}
          </div>
        </div>

        <div
          id="chat-thread-pane"
          className={`lg:col-span-7 xl:col-span-8 min-h-0 bg-indigo-900/60 rounded-3xl border border-white/10 shadow-xl flex flex-col overflow-hidden ${mobileActiveView === 'list' ? 'hidden lg:flex' : 'flex'
            }`}
        >
          {isEmptyChatState ? (
            <div className="flex-1 flex flex-col items-center justify-center space-y-4 p-8 text-center">
              <TeamChatEmptyIllustration />
              <div className="space-y-1.5 max-w-sm">
                <h4 className="font-black text-base text-white uppercase italic">
                  No conversations yet
                </h4>
                <p className="text-xs text-indigo-200/70">
                  Start a new chat with an athlete, or wait for someone to message you — new chats will show up here automatically.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  triggerHaptic('medium');
                  setShowNewChatModal(true);
                }}
                className="px-4 py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black uppercase italic text-xs rounded-2xl shadow-lg shadow-lime-400/20 transition flex items-center space-x-2"
              >
                <Plus className="w-4 h-4 stroke-[3]" />
                <span>Start New Chat</span>
              </button>
            </div>
          ) : (
            <>
              {/* Active Conversation Header */}
              <div className="p-3.5 sm:p-4 bg-indigo-950/95 border-b border-white/10 flex items-center justify-between shrink-0 shadow-md">
                <div className="flex items-center space-x-3 truncate">
                  {/* Mobile Back to Conversation List Button */}
                  <button
                    type="button"
                    onClick={() => {
                      triggerHaptic('light');
                      setMobileActiveView('list');
                    }}
                    className="lg:hidden p-2 rounded-xl bg-indigo-900 hover:bg-indigo-800 text-lime-400 border border-white/10"
                    title="Back to all chats"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>

                  {selectedTarget.category === 'direct' ? (
                    selectedTarget.avatar ? (
                      <img
                        src={selectedTarget.avatar}
                        alt={selectedTarget.name}
                        className="w-10 h-10 rounded-2xl object-cover ring-2 ring-lime-400 shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-2xl bg-lime-400 text-black flex items-center justify-center font-black shrink-0 text-sm">
                        {selectedTarget.name.charAt(0)}
                      </div>
                    )
                  ) : (
                    <div className="w-10 h-10 rounded-2xl bg-indigo-800 text-lime-400 flex items-center justify-center font-black shrink-0 text-lg border border-lime-400/30">
                      {selectedTarget.sportIcon || <Hash className="w-5 h-5" />}
                    </div>
                  )}

                  <div className="truncate min-w-0">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-black text-sm sm:text-base text-white truncate">
                        {selectedTarget.name}
                      </h3>
                      {selectedTarget.category === 'direct' && selectedTarget.isOnline && (
                        <span className="relative flex h-2.5 w-2.5 shrink-0" title="Active on platform">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-lime-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-lime-400 shadow-sm shadow-lime-400"></span>
                        </span>
                      )}
                      {selectedTarget.isPro && (
                        <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-lime-400/20 text-lime-300 border border-lime-400/30">
                          PRO
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-lime-300 font-semibold truncate flex items-center space-x-1.5">
                      {selectedTarget.category === 'direct' ? (
                        selectedUserPresence?.isTyping && selectedUserPresence.typingTargetId === user?.id ? (
                          <span className="text-lime-400 font-bold">Typing...</span>
                        ) : selectedTarget.isOnline ? (
                          <span className="text-lime-400 font-bold flex items-center">
                            <span className="w-1.5 h-1.5 rounded-full bg-lime-400 inline-block mr-1"></span>
                            Active Now
                          </span>
                        ) : (
                          <span className="text-indigo-300/70">Offline</span>
                        )
                      ) : (
                        <span>{selectedTarget.detail || selectedTarget.status || 'Active Channel'}</span>
                      )}
                      {selectedTarget.detail && selectedTarget.category === 'direct' && (
                        <span className="text-indigo-300/70 truncate">&bull; {selectedTarget.detail}</span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2 shrink-0">
                  <button
                    type="button"
                    onClick={(e) => togglePinTarget(selectedTarget.id, e)}
                    className={`p-2 rounded-xl border text-xs transition ${pinnedTargetIds.includes(selectedTarget.id)
                      ? 'bg-amber-400 text-black border-amber-300 shadow'
                      : 'bg-indigo-900/60 hover:bg-indigo-800 text-indigo-300 border-white/5'
                      }`}
                    title={pinnedTargetIds.includes(selectedTarget.id) ? 'Unpin chat' : 'Pin chat to top'}
                  >
                    <Pin className={`w-4 h-4 ${pinnedTargetIds.includes(selectedTarget.id) ? 'fill-black' : ''}`} />
                  </button>

                  {selectedTarget.category === 'direct' && onSendFriendRequest && (
                    <button
                      type="button"
                      onClick={() => {
                        triggerHaptic('medium');
                        if (isAlreadyFriends(selectedTarget.id) || sentFriendRequests.includes(selectedTarget.id)) {
                          showToast(`Already connected with ${selectedTarget.name}! 🤝`);
                          return;
                        }
                        onSendFriendRequest({
                          id: selectedTarget.id,
                          name: selectedTarget.name,
                          avatar: selectedTarget.avatar,
                          detail: selectedTarget.detail,
                        });
                        setSentFriendRequests((prev) => [...prev, selectedTarget.id]);
                        showToast(`Friend request sent to ${selectedTarget.name}! 🤝`);
                      }}
                      className={`hidden sm:flex items-center space-x-1 px-3 py-1.5 rounded-xl font-black text-[10px] uppercase italic transition border ${isAlreadyFriends(selectedTarget.id) || sentFriendRequests.includes(selectedTarget.id)
                        ? 'bg-emerald-950 text-emerald-300 border-emerald-500/40'
                        : 'bg-indigo-900/80 hover:bg-lime-400 hover:text-black text-indigo-100 border-white/10 hover:border-lime-400'
                        }`}
                    >
                      {isAlreadyFriends(selectedTarget.id) || sentFriendRequests.includes(selectedTarget.id) ? (
                        <>
                          <UserCheck className="w-3 h-3 text-emerald-400" />
                          <span>Connected ✓</span>
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-3 h-3" />
                          <span>Add Friend</span>
                        </>
                      )}
                    </button>
                  )}

                  {/* Clear Thread History */}
                  <button
                    type="button"
                    onClick={() => setShowClearHistoryConfirm(true)}
                    className="p-2 bg-indigo-900/60 hover:bg-rose-950 hover:text-rose-400 text-indigo-300 rounded-xl text-xs transition border border-white/5"
                    title="Clear thread history"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Clear History Confirmation Banner */}
              {showClearHistoryConfirm && (
                <div className="p-3 bg-rose-950/95 border-b border-rose-500/30 flex items-center justify-between text-xs animate-fadeIn shrink-0">
                  <span className="font-bold text-rose-200">Clear this conversation permanently? It will be removed from your chat list.</span>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={handleClearConversation}
                      className="px-3 py-1 bg-rose-600 hover:bg-rose-500 text-white font-black uppercase text-[10px] rounded-lg transition"
                    >
                      Confirm Clear
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowClearHistoryConfirm(false)}
                      className="px-3 py-1 bg-indigo-900 text-indigo-200 hover:text-white text-[10px] font-bold rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}


              <div ref={messagesContainerRef} className="min-h-0 flex-1 p-4 overflow-y-auto space-y-4">
                {isLoadingMessages ? (
                  <div className="h-full flex flex-col items-center justify-center space-y-2 text-indigo-300 py-12">
                    <Loader2 className="w-7 h-7 text-lime-400 animate-spin" />
                    <p className="text-xs font-bold uppercase tracking-wider text-lime-300">
                      Loading conversation messages...
                    </p>
                  </div>
                ) : currentMessages.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center space-y-3 py-10 text-center">
                    <TeamChatEmptyIllustration />
                    <div className="space-y-1 max-w-xs">
                      <h4 className="font-black text-sm text-white uppercase italic">
                        Start the conversation!
                      </h4>
                      <p className="text-xs text-indigo-200/70">
                        {selectedTarget.category === 'direct'
                          ? `Send a message to ${selectedTarget.name} to plan your next pickup run or coordinate game stats.`
                          : `Be the first to call out a game run in ${selectedTarget.name}!`}
                      </p>
                    </div>
                  </div>
                ) : (
                  currentMessages.map((msg) => {
                    const isMe = msg.senderId === user?.id || msg.senderId === 'current_user';
                    const detectedUrls = extractUrls(msg.text);
                    const reactions = messageReactions[msg.id] || [];

                    return (
                      <div
                        key={msg.id}
                        className={`flex items-start space-x-2.5 ${isMe ? 'flex-row-reverse space-x-reverse' : 'flex-row'} group`}
                      >
                        {/* Avatar */}
                        {msg.senderAvatar ? (
                          <img
                            src={msg.senderAvatar}
                            alt={msg.senderName}
                            className="w-8 h-8 rounded-full object-cover ring-1 ring-white/10 shrink-0 mt-0.5"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-lime-400 text-black font-black text-xs flex items-center justify-center shrink-0 mt-0.5">
                            {msg.senderName ? msg.senderName.charAt(0) : 'U'}
                          </div>
                        )}

                        {/* Bubble Content */}
                        <div className={`max-w-[85%] sm:max-w-[75%] flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                          {!isMe && (
                            <span className="text-[10px] font-black uppercase text-indigo-300 ml-1 mb-0.5">
                              {msg.senderName}
                            </span>
                          )}

                          <div
                            className={`p-3.5 rounded-2xl text-xs font-semibold leading-relaxed shadow-md relative ${isMe
                              ? 'bg-lime-400 text-black rounded-tr-none font-bold'
                              : 'bg-indigo-950 text-white rounded-tl-none border border-white/10'
                              }`}
                          >
                            {/* Formatted Text with clickable hyperlinks */}
                            <FormattedMessageText text={msg.text} isMe={isMe} />

                            {/* Rich Link / Highlight Preview Cards */}
                            {detectedUrls.length > 0 && (
                              <MessageLinkPreview
                                urls={detectedUrls}
                                isMe={isMe}
                                onOpenVideo={(url, title) => setVideoModal({ url, title })}
                                onNavigateToTab={onNavigateToTab}
                              />
                            )}

                            {/* Timestamp & Read Receipts */}
                            <div className={`flex items-center justify-end space-x-1 mt-1 text-[9px] font-mono ${isMe ? 'text-black/60 font-bold' : 'text-indigo-400'
                              }`}>
                              <span>{msg.timestamp || 'Just now'}</span>
                              {isMe && <CheckCheck className="w-3 h-3 text-black stroke-[2.5]" />}
                            </div>

                            {/* Active Emoji Reactions */}
                            {reactions.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5 pt-1.5 border-t border-black/10">
                                {reactions.map((r, rIdx) => {
                                  const userHas = r.users.includes(user?.id || 'me');
                                  return (
                                    <button
                                      key={rIdx}
                                      type="button"
                                      onClick={() => handleToggleReaction(msg.id, r.emoji)}
                                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold border transition flex items-center space-x-1 ${userHas
                                        ? 'bg-lime-400 text-black border-lime-300 shadow'
                                        : 'bg-indigo-950/80 text-white border-white/10'
                                        }`}
                                    >
                                      <span>{r.emoji}</span>
                                      <span>{r.count}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* Quick Emoji Reaction Trigger on Message Hover */}
                          <div className="flex items-center space-x-1 px-1 opacity-0 group-hover:opacity-100 transition">
                            <button
                              type="button"
                              onClick={() => setActiveEmojiPickerMsgId(activeEmojiPickerMsgId === msg.id ? null : msg.id)}
                              className="text-[11px] text-indigo-300 hover:text-lime-400 p-1 rounded transition"
                              title="Add reaction"
                            >
                              <Smile className="w-3.5 h-3.5" />
                            </button>
                            {activeEmojiPickerMsgId === msg.id && (
                              <div className="flex items-center space-x-1 bg-indigo-950 p-1 rounded-xl border border-white/10 shadow-lg">
                                {QUICK_EMOJIS.map((emoji) => (
                                  <button
                                    key={emoji}
                                    type="button"
                                    onClick={() => handleToggleReaction(msg.id, emoji)}
                                    className="hover:scale-125 transition text-xs p-1"
                                  >
                                    {emoji}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Quick Reply Chips Carousel */}
              <div className="px-3 py-2 bg-indigo-950/70 border-t border-white/5 flex items-center space-x-2 overflow-x-auto no-scrollbar shrink-0">
                <span className="text-[10px] font-mono text-lime-400 uppercase font-black shrink-0">
                  Quick:
                </span>
                {QUICK_REPLY_CHIPS.map((chip, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleQuickReply(chip.text)}
                    className="px-2.5 py-1 bg-indigo-900/80 hover:bg-lime-400 hover:text-black text-indigo-200 rounded-xl text-[11px] font-semibold border border-white/10 whitespace-nowrap transition shrink-0"
                  >
                    {chip.label}
                  </button>
                ))}
              </div>

              {/* Emoji Toolbar Popover */}
              {showEmojiBar && (
                <div className="p-2 bg-indigo-950 border-t border-white/10 flex items-center space-x-2 animate-fadeIn shrink-0">
                  <span className="text-xs text-indigo-300/70 font-bold px-1">Emojis:</span>
                  <div className="flex items-center space-x-2 overflow-x-auto">
                    {QUICK_EMOJIS.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => {
                          setText((prev) => `${prev} ${emoji}`);
                          setShowEmojiBar(false);
                        }}
                        className="text-lg hover:scale-125 transition p-1"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Message Input Bar Form */}
              <form onSubmit={handleSend} className="p-3 bg-indigo-950/90 border-t border-white/10 flex items-center space-x-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setShowEmojiBar(!showEmojiBar)}
                  className={`p-2.5 rounded-xl border transition ${showEmojiBar ? 'bg-lime-400 text-black border-lime-300' : 'bg-indigo-900 text-indigo-200 border-white/10 hover:text-white'
                    }`}
                  title="Add Emoji"
                >
                  <Smile className="w-4 h-4" />
                </button>

                <button
                  type="button"
                  onClick={handleShareLocation}
                  className="p-2.5 rounded-xl border border-white/10 bg-indigo-900 text-indigo-200 hover:text-lime-400 transition"
                  title="Share Court Location Pin"
                >
                  <MapPin className="w-4 h-4" />
                </button>

                <button
                  type="button"
                  onClick={toggleVoiceRecording}
                  className={`p-2.5 rounded-xl border transition ${isRecording
                    ? 'bg-rose-600 text-white animate-pulse border-rose-400'
                    : 'bg-indigo-900 text-indigo-200 border-white/10 hover:text-white'
                    }`}
                  title={isRecording ? 'Stop Voice Recording' : 'Voice Dictation'}
                >
                  <Mic className="w-4 h-4" />
                </button>

                <input
                  type="text"
                  placeholder={
                    selectedTarget.category === 'direct'
                      ? `Message ${selectedTarget.name}...`
                      : `Post message in ${selectedTarget.name}...`
                  }
                  value={text}
                  onChange={(e) => {
                    const nextText = e.target.value;
                    setText(nextText);
                    updateCurrentPresence(Boolean(nextText.trim()));
                  }}
                  className="flex-1 px-4 py-2.5 bg-indigo-900 text-white text-xs font-semibold rounded-2xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400 placeholder:text-indigo-300/50"
                />

                <button
                  type="submit"
                  disabled={!text.trim()}
                  className="px-4 py-2.5 bg-lime-400 hover:bg-lime-300 disabled:opacity-40 disabled:hover:bg-lime-400 text-black font-black italic uppercase text-xs rounded-2xl transition shadow flex items-center space-x-1"
                >
                  <span>Send</span>
                  <Send className="w-3.5 h-3.5" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* START NEW CHAT MODAL */}
      {showNewChatModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-indigo-950 border-2 border-lime-400/40 rounded-3xl p-5 max-w-lg w-full shadow-2xl space-y-4 animate-scaleUp text-white">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-xl bg-lime-400 text-black flex items-center justify-center font-black">
                  <UserPlus className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-black italic uppercase text-sm text-white">
                    Start a New Conversation
                  </h4>
                  <p className="text-[11px] text-indigo-300/70">
                    Select an athlete from the community to message
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowNewChatModal(false)}
                className="p-1 text-indigo-300 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search Input */}
            <div className="relative">
              <Search className="w-4 h-4 text-indigo-400 absolute left-3 top-3" />
              <input
                type="text"
                placeholder="Search athlete by name, handle, sport, school..."
                value={newChatSearch}
                onChange={(e) => setNewChatSearch(e.target.value)}
                className="w-full pl-9 pr-8 py-2.5 bg-indigo-900 text-white text-xs font-semibold rounded-2xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400 placeholder:text-indigo-300/50 shadow-inner"
              />
              {newChatSearch && (
                <button
                  type="button"
                  onClick={() => setNewChatSearch('')}
                  className="absolute right-2.5 top-3 text-indigo-300 hover:text-white"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Athletes Roster List */}
            <div className="overflow-y-auto space-y-1.5 max-h-72 pr-1">
              {filteredNewChatAthletes.map((athlete) => {
                const isOnline = isAthleteOnline(athlete.id, athlete);
                return (
                  <button
                    key={athlete.id}
                    type="button"
                    onClick={() => handleStartNewChatWithAthlete(athlete)}
                    className="w-full p-2.5 rounded-2xl bg-indigo-900/60 hover:bg-indigo-900 text-white border border-white/5 hover:border-lime-400/50 transition flex items-center justify-between group"
                  >
                    <div className="flex items-center space-x-3 truncate">
                      <div className="relative shrink-0">
                        {athlete.avatar ? (
                          <img
                            src={athlete.avatar}
                            alt={athlete.name}
                            className="w-10 h-10 rounded-full object-cover ring-1 ring-white/10"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-lime-400 text-black flex items-center justify-center font-bold text-xs">
                            {athlete.name.charAt(0)}
                          </div>
                        )}
                        {isOnline && (
                          <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-lime-400 border border-indigo-950 shadow-sm shadow-lime-400" />
                        )}
                      </div>

                      <div className="truncate text-left min-w-0">
                        <div className="flex items-center space-x-1.5">
                          <p className="text-xs font-black truncate text-white group-hover:text-lime-300">
                            {athlete.name}
                          </p>
                          {isOnline && (
                            <span className="relative flex h-2 w-2 shrink-0 ml-0.5" title="Online now">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-lime-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-lime-400"></span>
                            </span>
                          )}
                          {athlete.isPro && (
                            <span className="text-[9px] font-mono px-1 rounded bg-lime-400/20 text-lime-300">
                              PRO
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-indigo-300/80 truncate">
                          {athlete.position || 'Athlete'} &bull; {athlete.primarySport || 'Basketball'}{athlete.schoolOrLeague ? ` &bull; ${athlete.schoolOrLeague}` : ''}
                        </p>
                      </div>
                    </div>

                    <span className="px-2.5 py-1 rounded-xl text-[10px] font-black uppercase italic bg-indigo-950 text-lime-400 border border-lime-400/20 group-hover:bg-lime-400 group-hover:text-black transition shrink-0 ml-2">
                      Message 💬
                    </span>
                  </button>
                );
              })}

              {filteredNewChatAthletes.length === 0 && (
                <div className="p-6 text-center text-xs text-indigo-300/70">
                  No athletes found matching "{newChatSearch}"
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* VIDEO PREVIEW MODAL */}
      {videoModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-indigo-950 border border-lime-400/40 rounded-3xl p-5 max-w-2xl w-full shadow-2xl space-y-3 animate-scaleUp text-white">
            <div className="flex items-center justify-between border-b border-white/10 pb-2">
              <h4 className="font-black italic uppercase text-sm text-lime-400 truncate">
                {videoModal.title}
              </h4>
              <button
                type="button"
                onClick={() => setVideoModal(null)}
                className="p-1 text-indigo-300 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="relative aspect-video bg-black rounded-2xl overflow-hidden flex items-center justify-center">
              <img
                src="https://images.unsplash.com/photo-1519861531473-9200262188bf?auto=format&fit=crop&q=80&w=1000"
                alt="Highlight Video"
                className="w-full h-full object-cover opacity-80"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 space-y-2 p-4 text-center">
                <div className="w-14 h-14 rounded-full bg-lime-400 text-black flex items-center justify-center shadow-xl">
                  <Play className="w-7 h-7 fill-black ml-1" />
                </div>
                <p className="text-xs font-bold text-white max-w-sm">
                  Streetball Tournament Highlights &bull; Loaded on-demand
                </p>
                <a
                  href={videoModal.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-1.5 bg-lime-400 text-black font-black text-xs uppercase italic rounded-xl shadow mt-2"
                >
                  Watch Full Video ↗
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TOAST NOTIFICATION */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 right-6 bg-lime-400 text-black px-4 py-2.5 rounded-2xl font-black italic uppercase text-xs shadow-2xl z-50 flex items-center space-x-2 border border-black/10"
          >
            <Sparkles className="w-4 h-4 fill-black" />
            <span>{toastMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};